import * as Sentry from '@sentry/react';

type UserFlow =
  | 'auth'
  | 'payment'
  | 'account'
  | 'pay_fee_preferences'
  | 'acceptBid'
  | 'pay_mgmt_fee'
  | 'campaignOfferPurchase';
type Tags = {
  userFlow: UserFlow;
  [key: string]: string | number;
};

interface Context {
  user?: {
    email: string;
  };
  tags?: Tags;
  /**
   * The x-correlation-id sent with the failing request. The backend logs it to
   * Datadog as the `CorrelationId` facet, so attaching it here as a searchable
   * Sentry tag lets an issue be pivoted to the matching backend logs and back.
   */
  correlationId?: string;
}

export const logError = (error: string | Error | undefined, context: Context = {}) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('%clogError', 'background: red; color: white', error);
  }

  const { correlationId: explicitCorrelationId, ...restContext } = context;
  // Prefer an explicitly supplied id; otherwise fall back to one the axios
  // interceptor (annotateErrorWithCorrelationId) stamped onto the rejected
  // error, so REST failures link to Datadog without every caller passing it.
  const correlationId =
    explicitCorrelationId ??
    (typeof error === 'object' && error !== null ? (error as { correlationId?: string }).correlationId : undefined);

  const allContext = {
    ...restContext,
    tags: {
      ...restContext.tags,
      // additional data to be grouped as "tags"
      subscription: 'LogByConsole',
      ...(correlationId ? { correlationId } : {}),
    },
  };

  if (typeof error === 'string') {
    Sentry.captureMessage(error, allContext);
  } else {
    Sentry.captureException(error, allContext);
  }
};

const errors = new Set<string>();
export const logErrorOnce = (error: string) => {
  if (!errors.has(error)) {
    logError(error);
    errors.add(error);
  }
};

export const logger = (data: Record<string, unknown> & Context) => {
  logError(JSON.stringify(data), { tags: data.tags });
};
